import { useCallback, useMemo, useState } from "react"
import type { MatterFile } from "../contract"
import { anchorDoc, optionsFor, type Group, type Member, type SessionKind, type Shelf } from "./proceedings"

export interface Extra { file: MatterFile; hint?: string }

export interface ProceedingChoice {
  kind: SessionKind
  /** The proceedings this kind of session can start from, newest first. */
  options: Group[]
  chosen: Group | null
  choose: (id: string) => void
  /** The chosen proceeding's own papers, then the files that are not its own (unlinked, then evidence). */
  members: Member[]
  extras: Extra[]
  isChecked: (docId: string) => boolean
  toggle: (docId: string) => void
  /** The checked documents, in the order they should be read. */
  selectedIds: string[]
  /** Files still being read: no role yet. */
  reading: MatterFile[]
  /** The doc a Notice analysis or Appeal or not session reads. */
  anchor: string
}

/**
 * Which proceeding a session starts from and which of its documents it uses. One proceeding is chosen
 * without asking; several are offered, the newest first. Its papers are checked by default; unlinked
 * and evidence files are offered but left unchecked until the reader asks for them.
 */
export function useProceedingChoice(shelf: Shelf, kind: SessionKind, opts: { evidenceChecked?: boolean } = {}): ProceedingChoice {
  const options = useMemo(() => optionsFor(shelf, kind), [shelf, kind])
  const [picked, setPicked] = useState("")
  const chosen = options.find((g) => g.proceeding.id === picked) ?? options[0] ?? null
  // Per proceeding, the reader's own ticks; anything not ticked either way takes its default.
  const [ticks, setTicks] = useState<Record<string, boolean>>({})
  const pid = chosen?.proceeding.id ?? ""
  const key = (docId: string) => `${pid}:${docId}`

  const members = chosen?.members ?? []
  const extras: Extra[] = useMemo(() => {
    if (!chosen) return []
    return [
      ...shelf.unlinked.map((file) => ({ file, hint: `Not linked to this ${chosen.proceeding.kind === "scn" ? "SCN" : "order"}` })),
      ...shelf.evidence.map((file) => ({ file })),
    ]
  }, [chosen, shelf])
  const evidenceIds = useMemo(() => new Set(shelf.evidence.map((f) => f.doc_id)), [shelf])

  const isChecked = useCallback((docId: string) => {
    const t = ticks[`${pid}:${docId}`]
    if (t !== undefined) return t
    if (members.some((m) => m.file.doc_id === docId)) return true
    return !!opts.evidenceChecked && evidenceIds.has(docId)
  }, [ticks, pid, members, opts.evidenceChecked, evidenceIds])

  const toggle = useCallback((docId: string) => setTicks((cur) => ({ ...cur, [key(docId)]: !isChecked(docId) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isChecked, pid])

  const selectedIds = [...members.map((m) => m.file.doc_id), ...extras.map((e) => e.file.doc_id)].filter(isChecked)
  return {
    kind, options, chosen, choose: setPicked, members, extras, isChecked, toggle, selectedIds,
    reading: shelf.reading, anchor: chosen ? anchorDoc(chosen, kind) : "",
  }
}
